// src/components/charts/ActivityHeatmap.jsx
//
// 7 × 24 heatmap of created_at, bucketed by browser-local day-of-week and hour.
// Hand-rolled SVG (no recharts heatmap primitive; rejected adding a new dep).
// Sample-based: pass `runs` (last 200), not stats.
//
// Timezone choice: browser local time. Users care about "when work happens for me",
// not when the server received the row. The BE sends ISO UTC; we convert.
//
import React from 'react';

const FONT = "'DM Sans', sans-serif";
const DAY_LABELS_IT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
// JS getDay(): 0=Sun..6=Sat. We want 0=Mon..6=Sun.
const JS_DAY_TO_MON_FIRST = [6, 0, 1, 2, 3, 4, 5];

/**
 * Bucket rows by day-of-week × hour-of-day (local time).
 * Returns { grid: 7×24 number[][], max: number }.
 * Exported for unit testing.
 */
export function bucketActivity(runs) {
  const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
  if (!Array.isArray(runs)) return { grid, max: 0 };
  let max = 0;
  for (const r of runs) {
    if (!r?.created_at) continue;
    const d = new Date(r.created_at);
    if (Number.isNaN(d.getTime())) continue;
    const dow = JS_DAY_TO_MON_FIRST[d.getDay()];
    const hr  = d.getHours();
    grid[dow][hr] += 1;
    if (grid[dow][hr] > max) max = grid[dow][hr];
  }
  return { grid, max };
}

function cellColor(value, max) {
  if (max === 0 || value === 0) return '#f3f4f6';
  const t = value / max;
  // Single-hue green ramp.
  if (t < 0.25) return '#d1fae5';
  if (t < 0.5)  return '#a7f3d0';
  if (t < 0.75) return '#34d399';
  return '#10b981';
}

export default function ActivityHeatmap({ runs }) {
  const { grid, max } = bucketActivity(runs);
  if (max === 0) return null;
  const cell = 14;
  const gap = 2;
  const labelW = 32;
  const hoursW = 24 * (cell + gap);
  const daysH = 7 * (cell + gap);

  return (
    <div style={{ fontFamily: FONT, fontSize: 11, color: '#6c6c6d', overflowX: 'auto' }}>
      <svg
        width={labelW + hoursW}
        height={daysH + 18}
        role="img"
        aria-label="Heatmap dell'attività per giorno e ora"
      >
        {/* hour labels (top, every 3h) */}
        {Array.from({ length: 24 }, (_, h) => (h % 3 === 0 ? (
          <text key={`hl-${h}`} x={labelW + h * (cell + gap) + cell / 2} y={12} textAnchor="middle" fill="#6c6c6d">
            {h}
          </text>
        ) : null))}
        {/* day labels (left) */}
        {DAY_LABELS_IT.map((lbl, dow) => (
          <text key={`dl-${dow}`} x={labelW - 6} y={18 + dow * (cell + gap) + cell - 2} textAnchor="end" fill="#6c6c6d">
            {lbl}
          </text>
        ))}
        {/* cells */}
        {grid.map((row, dow) => row.map((value, hr) => (
          <rect
            key={`c-${dow}-${hr}`}
            x={labelW + hr * (cell + gap)}
            y={18 + dow * (cell + gap)}
            width={cell}
            height={cell}
            fill={cellColor(value, max)}
          >
            <title>{`${DAY_LABELS_IT[dow]} h${hr}: ${value}`}</title>
          </rect>
        )))}
      </svg>
    </div>
  );
}
